import { Alert, Button, Card, Group, Loader, Stack, Switch, Text } from '@mantine/core';
import { IconInfoCircle, IconSettings } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import Algorithms from '../../constants/Algorithms';

// The platform-wide feed-type allowlist. Unlike FeedBanditPanel, which is
// per-domain, this reads and writes /api/admin/feed-bandit/allowlist — an
// endpoint with no domain in its path — so it owns its own tab and takes no
// props. GET resolves { universe, enabled }; PUT takes { algorithms }.
const allowlistUrl = '/api/admin/feed-bandit/allowlist';

const FeedTypeAllowlistPanel = () => {
  const fetch = useFetch();

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(allowlistUrl);
      // Tolerate either a flat { universe, enabled } body or one nested under
      // an `allowlist` key (the shape the domain-scoped feed-bandit GET uses
      // for the same data) so a backend that resolves this route with the
      // nested shape doesn't silently read as an empty registry.
      setState(res?.allowlist ?? res);
    } catch (err) {
      setError(err.message || 'Failed to load the feed types');
    } finally {
      setLoading(false);
    }
  }, [fetch]);

  useEffect(() => {
    load();
  }, [load]);

  // Flip one strategy on/off, sending the resulting full enabled set.
  const toggleFeedType = async (algorithm, on) => {
    const current = state?.enabled || [];
    const next = on ? [...new Set([...current, algorithm])] : current.filter((a) => a !== algorithm);
    setSaving(true);
    setError(null);
    try {
      await fetch(allowlistUrl, { method: 'PUT', body: JSON.stringify({ algorithms: next }) });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update the feed types');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card withBorder>
        <Group>
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading feed types…
          </Text>
        </Group>
      </Card>
    );
  }

  // A failed load leaves state null — show the error rather than the empty-state
  // copy ("No eligible feed types found."), which would misread as a healthy
  // empty registry.
  if (error && !state) {
    return (
      <Card withBorder>
        <Alert color="red" variant="light" icon={<IconInfoCircle size={16} />}>
          {error}
        </Alert>
        <Button
          mt="sm"
          size="xs"
          variant="light"
          onClick={() => {
            setLoading(true);
            load();
          }}
        >
          Retry
        </Button>
      </Card>
    );
  }

  // Default both shapes individually: useFetch resolves an empty object on a
  // 202/204 or an unparseable body, so `state` can be truthy while either key
  // is missing.
  const universe = state?.universe || [];
  const enabled = state?.enabled || [];

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Group gap="xs">
          <IconSettings size={18} />
          <Text fw={600}>Eligible feed types</Text>
          <Text size="xs" c="dimmed">
            {`${enabled.length} / ${universe.length} enabled`}
          </Text>
        </Group>
      </Card.Section>

      {error && (
        <Card.Section inheritPadding py="xs">
          <Alert color="red" variant="light" icon={<IconInfoCircle size={16} />}>
            {error}
          </Alert>
        </Card.Section>
      )}

      <Card.Section inheritPadding py="sm" withBorder>
        <Alert color="yellow" variant="light" mb="sm" icon={<IconInfoCircle size={16} />}>
          Applies to{' '}
          <Text span fw={700}>
            every domain
          </Text>
          . This list is discovered automatically — it&apos;s every feed type that ranks the catalog the same for all
          shoppers (so it can be a fair arm on web, email and search). Switching one off stops it being added to{' '}
          <Text span fw={700}>
            new
          </Text>{' '}
          tests; a domain already testing it drops it the next time its pool or switch is changed.
        </Alert>
        {universe.length === 0 ? (
          <Text size="sm" c="dimmed">
            No eligible feed types found.
          </Text>
        ) : (
          <Stack gap="xs">
            {universe.map((algo) => (
              <Switch
                key={algo}
                size="sm"
                label={Algorithms[algo]?.name || algo}
                checked={enabled.includes(algo)}
                disabled={saving}
                onChange={(e) => toggleFeedType(algo, e.currentTarget.checked)}
              />
            ))}
          </Stack>
        )}
      </Card.Section>
    </Card>
  );
};

export default FeedTypeAllowlistPanel;
